import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Wallet.css';

function Wallet() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10000); // 10 seconds default
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Setup auto-refresh interval
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchWallets(false); // Silent refresh
      }, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval]);

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await axios.get('https://trcsite.net/api/wallet', {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Wallet API Response:', response.data);
      
      let walletData = [];
      
      if (response.data && Array.isArray(response.data)) {
        walletData = response.data;
      } else if (response.data && response.data.wallets && Array.isArray(response.data.wallets)) {
        walletData = response.data.wallets;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        walletData = response.data.data;
      } else {
        walletData = response.data.wallets || response.data.data || [];
      }
      
      if (!Array.isArray(walletData)) {
        console.warn('Wallet data is not an array, converting to empty array:', walletData);
        walletData = [];
      }
      
      setWallets(walletData);
      setLastUpdated(new Date().toLocaleTimeString());
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching wallets:', error);
      setError('Failed to load wallet data: ' + (error.response?.data?.error || error.message));
      setWallets([]);
      setLoading(false);
    }
  };

  const handleDeposit = () => {
    navigate('/deposit');
  };

  const handleWithdraw = () => {
    navigate('/withdraw');
  };

  const handleQuickDeposit = (currency) => {
    navigate('/deposit', { state: { preselectedCurrency: currency } });
  };

  const handleQuickWithdraw = (currency) => {
    navigate('/withdraw', { state: { preselectedCurrency: currency } });
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handleIntervalChange = (interval) => {
    setRefreshInterval(interval);
  };

  const formatBalance = (balance, currency) => {
    const balanceNum = parseFloat(balance) || 0;
    
    if (currency === 'USDT') {
      return balanceNum.toLocaleString(undefined, { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    } else {
      return balanceNum.toLocaleString(undefined, { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 8 
      });
    }
  };

  const getTotalBalance = () => {
    return wallets.reduce((total, wallet) => {
      const balance = parseFloat(wallet.balance) || 0;
      return total + balance;
    }, 0);
  };

  const getTotalAvailable = () => {
    return wallets.reduce((total, wallet) => {
      const balance = parseFloat(wallet.balance) || 0;
      const locked = parseFloat(wallet.locked) || 0;
      return total + (balance - locked);
    }, 0);
  };

  const getFrozenWalletsCount = () => {
    return wallets.filter(wallet => wallet.is_frozen).length;
  };

  // Check if wallet actions are disabled due to freeze
  const isWalletActionDisabled = (wallet) => {
    return wallet.is_frozen;
  };

  if (loading) {
    return (
      <div className="wallet-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading wallet data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wallet-container">
        <div className="error-message">
          <h3>Unable to Load Wallet</h3>
          <p>{error}</p>
          <button onClick={fetchWallets} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-container">
      {/* Header Section */}
      <div className="wallet-header">
        <div className="wallet-title-section">
          <h1>My Wallet</h1>
          <p>Manage your cryptocurrency balances and assets</p>
        </div>
        
        <div className="wallet-controls">
          {/* Auto-refresh Controls */}
          <div className="auto-refresh-controls">
            <div className="refresh-toggle">
              <label className="toggle-label">
                <input 
                  type="checkbox" 
                  checked={autoRefresh}
                  onChange={toggleAutoRefresh}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
                Auto Refresh
              </label>
            </div>
            
            {autoRefresh && (
              <div className="interval-selector">
                <select 
                  value={refreshInterval} 
                  onChange={(e) => handleIntervalChange(Number(e.target.value))}
                  className="interval-select"
                >
                  <option value={5000}>5 seconds</option>
                  <option value={10000}>10 seconds</option>
                  <option value={30000}>30 seconds</option>
                  <option value={60000}>1 minute</option>
                </select>
              </div>
            )}
          </div>

          <div className="wallet-actions">
            <button onClick={fetchWallets} className="btn btn-outline">
              <span className={`refresh-icon ${autoRefresh ? 'spinning' : ''}`}>🔄</span>
              Refresh Now
            </button>
            <button onClick={handleDeposit} className="btn btn-primary">
              <span>💰</span>
              Deposit
            </button>
            <button onClick={handleWithdraw} className="btn btn-outline">
              <span>📤</span>
              Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-item">
          <span className="status-label">Auto Refresh:</span>
          <span className={`status-value ${autoRefresh ? 'status-active' : 'status-inactive'}`}>
            {autoRefresh ? 'ON' : 'OFF'}
          </span>
        </div>
        <div className="status-item">
          <span className="status-label">Next Update:</span>
          <span className="status-value">
            {autoRefresh ? `Every ${refreshInterval/1000}s` : 'Manual'}
          </span>
        </div>
        <div className="status-item">
          <span className="status-label">Last Updated:</span>
          <span className="status-value">{lastUpdated}</span>
        </div>
        {getFrozenWalletsCount() > 0 && (
          <div className="status-item">
            <span className="status-label">Frozen Wallets:</span>
            <span className="status-value status-warning">
              {getFrozenWalletsCount()} frozen
            </span>
          </div>
        )}
      </div>

      {/* Summary Section */}
      {wallets.length > 0 && (
        <div className="wallet-summary">
          <div className="summary-item">
            <div className="summary-label">Total Balance</div>
            <div className="summary-value">${getTotalBalance().toLocaleString()}</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Available</div>
            <div className="summary-value" style={{ color: 'var(--success-color)' }}>
              ${getTotalAvailable().toLocaleString()}
            </div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Total Wallets</div>
            <div className="summary-value">{wallets.length}</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Frozen Wallets</div>
            <div className={`summary-value ${getFrozenWalletsCount() > 0 ? 'summary-warning' : ''}`}>
              {getFrozenWalletsCount()}
            </div>
          </div>
        </div>
      )}

      {/* Wallet Cards */}
      {wallets.length > 0 ? (
        <div className="wallet-cards">
          {wallets.map(wallet => (
            <div 
              key={wallet.currency || wallet.id} 
              className={`wallet-card ${wallet.is_frozen ? 'wallet-frozen' : ''}`}
            >
              {/* Frozen Banner */}
              {wallet.is_frozen && (
                <div className="frozen-banner">
                  <span className="frozen-icon">❄️</span>
                  <span className="frozen-text">WALLET FROZEN</span>
                </div>
              )}
              
              <div className="wallet-card-header">
                <div className="wallet-currency">
                  {wallet.currency}
                  {wallet.is_frozen && (
                    <span className="frozen-badge">FROZEN</span>
                  )}
                  <span style={{ 
                    fontSize: '14px', 
                    color: 'var(--text-muted)',
                    fontWeight: 'normal'
                  }}>
                    {wallet.name || ''}
                  </span>
                </div>
                <div className="wallet-quick-actions">
                  <button 
                    onClick={() => handleQuickDeposit(wallet.currency)}
                    className={`btn-icon ${isWalletActionDisabled(wallet) ? 'btn-disabled' : ''}`}
                    title={isWalletActionDisabled(wallet) ? "Wallet frozen - deposits disabled" : "Quick Deposit"}
                    disabled={isWalletActionDisabled(wallet)}
                  >
                    ⬇️
                  </button>
                  <button 
                    onClick={() => handleQuickWithdraw(wallet.currency)}
                    className={`btn-icon ${isWalletActionDisabled(wallet) ? 'btn-disabled' : ''}`}
                    title={isWalletActionDisabled(wallet) ? "Wallet frozen - withdrawals disabled" : "Quick Withdraw"}
                    disabled={isWalletActionDisabled(wallet)}
                  >
                    ⬆️
                  </button>
                </div>
              </div>
              
              <div className={`wallet-balance ${wallet.is_frozen ? 'balance-frozen' : ''}`}>
                {formatBalance(wallet.balance, wallet.currency)} {wallet.currency}
              </div>
              
              <div className="wallet-details">
                <div className="wallet-available">
                  <span>Available:</span>
                  <span>
                    {formatBalance((parseFloat(wallet.balance) || 0) - (parseFloat(wallet.locked) || 0), wallet.currency)} {wallet.currency}
                  </span>
                </div>
                <div className="wallet-locked">
                  <span>Locked:</span>
                  <span>{formatBalance(wallet.locked, wallet.currency)} {wallet.currency}</span>
                </div>
                
                {/* Freeze Details */}
                {wallet.is_frozen && (
                  <div className="freeze-details">
                    <div className="freeze-reason">
                      <span>Reason:</span>
                      <span className="freeze-reason-text">
                        {wallet.frozen_reason || 'Security Hold'}
                      </span>
                    </div>
                    {wallet.frozen_at && (
                      <div className="freeze-date">
                        <span>Frozen Since:</span>
                        <span>{new Date(wallet.frozen_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Real-time update indicator */}
              {autoRefresh && !wallet.is_frozen && (
                <div className="real-time-indicator">
                  <div className="pulse-dot"></div>
                  <span>Live</span>
                </div>
              )}

              {/* Frozen indicator */}
              {wallet.is_frozen && (
                <div className="frozen-indicator">
                  <div className="frozen-dot"></div>
                  <span>Frozen</span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">💼</div>
          <h3>No Wallets Found</h3>
          <p>You don't have any cryptocurrency wallets yet.</p>
          <div className="empty-state-actions">
            <button onClick={handleDeposit} className="btn btn-primary">
              Make First Deposit
            </button>
            <button onClick={fetchWallets} className="btn btn-outline">
              Check Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Wallet;